/**
 * Launch Kit workbook preview.
 *
 * The "implementation consultant" screen: upload the CairnOS Launch Kit
 * workbook, get a rich readiness report (per-entity status, blockers,
 * warnings, and info-level suggestions). No writes to the database —
 * commit is a future block. Diagnostics are structured (code / severity
 * / tab / row / column / fix) so we can filter, group, export, and later
 * hand off to AI without rebuilding the engine.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useRef, useState } from "react";
import { AlertCircle, ArrowLeft, CheckCircle2, Download, FileSpreadsheet, Info, Loader2, Upload } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { previewLaunchKit } from "@/lib/launch-kit/preview.functions";
import type { Diagnostic, EntityReadiness, ReadinessReport, Severity, TabKey } from "@/lib/launch-kit/diagnostics-types";

export const Route = createFileRoute("/_authenticated/app/implementation/launch-kit-preview")({
  component: LaunchKitPreviewPage,
  head: () => ({
    meta: [
      { title: "Launch Kit preview · CairnOS" },
      { name: "description", content: "Upload the CairnOS Launch Kit workbook to see exactly what's ready to import and what needs to be fixed — before any data is written." },
    ],
  }),
});

async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function SeverityBadge({ severity }: { severity: Severity }) {
  if (severity === "blocker") {
    return <Badge variant="destructive" className="uppercase tracking-wide">Blocker</Badge>;
  }
  if (severity === "warning") {
    return <Badge className="bg-amber-500/15 text-amber-700 hover:bg-amber-500/20 border border-amber-500/30 uppercase tracking-wide">Warning</Badge>;
  }
  return <Badge variant="secondary" className="uppercase tracking-wide">Info</Badge>;
}

function EntityStatusBadge({ status }: { status: EntityReadiness["status"] }) {
  if (status === "ready") return <Badge className="bg-emerald-500/15 text-emerald-700 border border-emerald-500/30">Ready to import</Badge>;
  if (status === "blocked") return <Badge variant="destructive">Blocked</Badge>;
  if (status === "warnings") return <Badge className="bg-amber-500/15 text-amber-700 border border-amber-500/30">Warnings only</Badge>;
  return <Badge variant="secondary">Empty</Badge>;
}

function downloadCsv(rows: Diagnostic[], filename: string) {
  const headers = ["code","severity","tab","row","column","message","expected","found","fix","context"];
  const esc = (v: unknown) => {
    const s = v === null || v === undefined ? "" : typeof v === "object" ? JSON.stringify(v) : String(v);
    return `"${s.replace(/"/g, '""')}"`;
  };
  const csv = [
    headers.join(","),
    ...rows.map((d) => [d.code, d.severity, d.tab ?? "", d.row ?? "", d.column ?? "", d.message, d.expected ?? "", d.found ?? "", d.fix, d.context ?? ""].map(esc).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

function LaunchKitPreviewPage() {
  const runPreview = useServerFn(previewLaunchKit);
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<ReadinessReport | null>(null);

  // Filters
  const [severityFilter, setSeverityFilter] = useState<"all"|Severity>("all");
  const [tabFilter, setTabFilter] = useState<"all"|TabKey>("all");
  const [search, setSearch] = useState("");

  async function onUpload(f: File) {
    setBusy(true); setError(null); setReport(null); setFile(f);
    try {
      if (!/\.xlsx$/i.test(f.name)) {
        throw new Error("Please upload the Launch Kit .xlsx workbook.");
      }
      const contentBase64 = await fileToBase64(f);
      const r = await runPreview({ data: { filename: f.name, contentBase64 } });
      setReport(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong parsing the workbook.");
    } finally {
      setBusy(false);
    }
  }

  const filtered = useMemo(() => {
    if (!report) return [];
    return report.diagnostics.filter((d) => {
      if (severityFilter !== "all" && d.severity !== severityFilter) return false;
      if (tabFilter !== "all" && d.tab !== tabFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const hay = `${d.code} ${d.message} ${d.fix} ${d.column ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [report, severityFilter, tabFilter, search]);

  // Group filtered diagnostics by tab so customers can work through one entity
  // at a time. Tab-level (row=null, tab=null) findings land in an "Other" group.
  const grouped = useMemo(() => {
    if (!report) return [] as Array<{ tab: TabKey | null; label: string; items: Diagnostic[] }>;
    const order: Array<TabKey | null> = report.entities.map((e) => e.tab);
    const buckets = new Map<TabKey | "__other__", Diagnostic[]>();
    for (const d of filtered) {
      const key = (d.tab ?? "__other__") as TabKey | "__other__";
      const arr = buckets.get(key) ?? [];
      arr.push(d);
      buckets.set(key, arr);
    }
    const groups: Array<{ tab: TabKey | null; label: string; items: Diagnostic[] }> = [];
    for (const t of order) {
      const items = buckets.get(t as TabKey);
      if (items && items.length) {
        const label = report.entities.find((e) => e.tab === t)?.label ?? String(t);
        groups.push({ tab: t, label, items });
      }
    }
    const other = buckets.get("__other__");
    if (other && other.length) groups.push({ tab: null, label: "Workbook", items: other });
    return groups;
  }, [report, filtered]);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3 text-sm text-muted-foreground">
        <Link to="/app/implementation/workspace/data" className="inline-flex items-center gap-1 hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to Data track
        </Link>
      </div>

      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Launch Kit preview</h1>
        <p className="text-muted-foreground mt-2 max-w-3xl">
          Upload your filled-in Launch Kit workbook. You'll get a full readiness report — every issue with a clear
          fix — before any data is written. Nothing here touches the database.
        </p>
      </div>

      {!report && (
        <Card className="p-8">
          <div className="flex flex-col items-center text-center gap-4">
            <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center">
              <FileSpreadsheet className="h-7 w-7 text-muted-foreground" />
            </div>
            <div>
              <div className="font-medium">Upload the CairnOS Launch Kit workbook (.xlsx)</div>
              <div className="text-sm text-muted-foreground mt-1">
                Don't have one yet?{" "}
                <a href="/launch-kit/CairnOS-Launch-Kit.xlsx" className="underline">Download the latest template</a>.
              </div>
            </div>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); }}
            />
            <Button onClick={() => inputRef.current?.click()} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              {busy ? "Analyzing…" : "Choose workbook"}
            </Button>
            {file && !busy && !report && !error && (
              <div className="text-xs text-muted-foreground">Selected: {file.name}</div>
            )}
            {error && (
              <div className="text-sm text-destructive flex items-center gap-2">
                <AlertCircle className="h-4 w-4" /> {error}
              </div>
            )}
          </div>
        </Card>
      )}

      {report && (
        <>
          <Card className="p-6">
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Workbook</div>
                <div className="font-medium">{report.filename}</div>
                {report.workbookVersion && (
                  <div className="text-xs text-muted-foreground">Version {report.workbookVersion}</div>
                )}
              </div>
              <div className="flex-1 min-w-64">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-medium">{report.readinessPct}% of your workbook is ready</span>
                  <span className="text-muted-foreground">{report.totalRows.toLocaleString()} rows</span>
                </div>
                <Progress value={report.readinessPct} />
                <div className="flex items-center gap-4 mt-3 text-sm">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-destructive" />
                    <strong>{report.totalBlockers}</strong> blocking {report.totalBlockers === 1 ? "issue" : "issues"}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-amber-500" />
                    <strong>{report.totalWarnings}</strong> {report.totalWarnings === 1 ? "warning" : "warnings"}{" "}
                    <span className="text-muted-foreground">(won't block import)</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-sky-500" />
                    <strong>{report.totalInfo}</strong> {report.totalInfo === 1 ? "suggestion" : "suggestions"}
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => downloadCsv(report.diagnostics, `launch-kit-diagnostics-${new Date().toISOString().slice(0,10)}.csv`)}>
                  <Download className="h-4 w-4 mr-2" /> Export CSV
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setReport(null); setFile(null); setError(null); }}>
                  Upload another
                </Button>
              </div>
            </div>
          </Card>

          <div>
            <h2 className="text-lg font-semibold mb-3">Readiness by entity</h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {report.entities.map((e) => (
                <Card key={e.tab} className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium">{e.label}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{e.rowCount.toLocaleString()} rows</div>
                    </div>
                    <EntityStatusBadge status={e.status} />
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-4 text-xs">
                    <div><div className="font-semibold">{e.blockers}</div><div className="text-muted-foreground">Blockers</div></div>
                    <div><div className="font-semibold">{e.warnings}</div><div className="text-muted-foreground">Warnings</div></div>
                    <div><div className="font-semibold">{e.info}</div><div className="text-muted-foreground">Info</div></div>
                  </div>
                  {e.rowCount > 0 && (
                    <button
                      className="text-xs text-muted-foreground hover:text-foreground mt-3 underline"
                      onClick={() => { setTabFilter(e.tab); setSeverityFilter("all"); }}
                    >
                      Filter issues to this tab →
                    </button>
                  )}
                </Card>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <h2 className="text-lg font-semibold">Diagnostics</h2>
              <div className="flex items-center gap-2 flex-wrap">
                <Input
                  placeholder="Search code, message, column…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-64"
                />
                <Select value={severityFilter} onValueChange={(v) => setSeverityFilter(v as "all" | Severity)}>
                  <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All severities</SelectItem>
                    <SelectItem value="blocker">Blockers</SelectItem>
                    <SelectItem value="warning">Warnings</SelectItem>
                    <SelectItem value="info">Info</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={tabFilter} onValueChange={(v) => setTabFilter(v as "all" | TabKey)}>
                  <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All tabs</SelectItem>
                    {report.entities.map((e) => (
                      <SelectItem key={e.tab} value={e.tab}>{e.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {filtered.length === 0 ? (
              <Card className="p-10 text-center">
                <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
                <div className="font-medium">Nothing matches these filters</div>
                <div className="text-sm text-muted-foreground mt-1">
                  {report.totalBlockers === 0 ? "No blocking issues in this workbook." : "Clear filters to see all diagnostics."}
                </div>
              </Card>
            ) : (
              <div className="space-y-6">
                {grouped.map((group) => {
                  const entity = group.tab ? report.entities.find((e) => e.tab === group.tab) : null;
                  const blockers = group.items.filter((d) => d.severity === "blocker").length;
                  const warnings = group.items.filter((d) => d.severity === "warning").length;
                  const info = group.items.filter((d) => d.severity === "info").length;
                  return (
                    <div key={group.tab ?? "__other__"}>
                      <div className="flex items-baseline justify-between mb-2">
                        <div className="flex items-baseline gap-2">
                          <h3 className="text-base font-semibold">{group.label}</h3>
                          {entity && (
                            <span className="text-xs text-muted-foreground">
                              {entity.rowCount.toLocaleString()} rows
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {blockers > 0 && <span className="mr-3"><strong className="text-destructive">{blockers}</strong> blocking</span>}
                          {warnings > 0 && <span className="mr-3"><strong className="text-amber-600">{warnings}</strong> warnings</span>}
                          {info > 0 && <span><strong className="text-sky-600">{info}</strong> info</span>}
                        </div>
                      </div>
                      <Card className="overflow-hidden">
                        <div className="divide-y">
                          {group.items.map((d, i) => (
                            <div key={i} className="p-4 flex gap-4">
                              <div className="pt-0.5">
                                {d.severity === "blocker" ? (
                                  <AlertCircle className="h-5 w-5 text-destructive" />
                                ) : d.severity === "warning" ? (
                                  <AlertCircle className="h-5 w-5 text-amber-500" />
                                ) : (
                                  <Info className="h-5 w-5 text-sky-500" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <SeverityBadge severity={d.severity} />
                                  {d.row !== null && <span className="text-xs text-muted-foreground">Row {d.row}</span>}
                                  {d.column && <span className="text-xs text-muted-foreground">Column <code className="text-xs">{d.column}</code></span>}
                                  <code className="ml-auto text-[10px] text-muted-foreground">{d.code}</code>
                                </div>
                                <div className="mt-1.5 font-medium">{d.message}</div>
                                {(d.expected || d.found) && (
                                  <div className="mt-3 grid gap-2 sm:grid-cols-2 text-sm">
                                    {d.expected && (
                                      <div className="rounded-md border bg-muted/30 p-2.5">
                                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Expected</div>
                                        <div className="mt-0.5 font-mono text-xs whitespace-pre-wrap break-words">{d.expected}</div>
                                      </div>
                                    )}
                                    {d.found && (
                                      <div className="rounded-md border bg-muted/30 p-2.5">
                                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Found</div>
                                        <div className="mt-0.5 font-mono text-xs whitespace-pre-wrap break-words">{d.found}</div>
                                      </div>
                                    )}
                                  </div>
                                )}
                                <div className="text-sm mt-3">
                                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Suggested fix</span>
                                  <div className="mt-0.5 text-foreground">{d.fix}</div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </Card>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <Card className="p-4 bg-muted/40">
            <div className="flex items-start gap-3 text-sm text-muted-foreground">
              <Info className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <span className="font-medium text-foreground">Preview only.</span>{" "}
                Nothing has been written to your tenant. When the preview is clean, the commit step will let
                you import in blocks (Customers &amp; Facilities first, then SKUs, then Opening Inventory).
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}